import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { connect } from 'react-redux';
import clsx from 'clsx';
import { bindActionCreators } from 'redux';
import ReactCardFlip from 'react-card-flip';
import { sendAnalyticsEvent } from '../../../../../actions/reports';
import QQHeader from '../QQHeader';
import QQBarAndLabel from '../QQBarAndLabel';
import QQLegend from '../QQLegend';
import Loader from '../../../Loader';
import QQScoreHistogramChart from '../QQScoreHistogramChart/QQScoreHistogramChart';
import QQLoginPieChart from '../QQLoginPieChart/QQLoginPieChart';
import MLDialog from '../../components/MLDialog';
import {
  WIDGET_TYPE_LOGIN_PIE_CHART,
  WIDGET_TYPE_ASSIGNMENT_SCORE_HISTOGRAM_CHART,
  WIDGET_TYPE_TIME_SCORE_CHART,
  WIDGET_TYPE_TIME_ATTEMPTS_CHART,
  WIDGET_TYPE_COMPLETED_ASSIGNMENT,
  WIDGET_TYPE_SRL_CONFIDENCE_CHART,
  WIDGET_TYPE_SRL_HOURS_CHART,
  WIDGET_TYPE_SRL_GOAL_CHART,
  WIDGET_TYPE_SRL_CHALLENGES_CHART,
  WIDGET_TYPE_SRL_SPACING_PACING_CHART,
  WIDGET_TYPE_SRL_CHALLENGE_AND_RECALL_CHART,
  WIDGET_TYPE_SRL_HELP_AND_COLLABORATION,
  WIDGET_TYPE_SRL_ENVIRONMENT_AND_MINDSET,
  WIDGET_TYPE_SRL_ACADEMICS_CHART,
  WIDGET_TYPE_SRL_GROWTH_CHART,
  isSRLWidget as checkSRLWidget,
  isSRLWidgetSummarizable,
  isAssignmentPerfWidget,
} from '../../../../constants';
import QQTimeVsScoreCalcInfo from '../QQTimeVsScoreCalcInfo/QQTimeVsScoreCalcInfo';

import QQInsightsFooterActions from '../QQInsightsFooterActions/QQInsightsFooterActions';
import QQTimeOnTaskChart from '../QQTimeOnTask/QQTimeOnTask';
import QQCompletedAssignment from '../QQInsightsCompletedAssignment';
import SRLConfidenceContent from '../SRLConfidenceContent/SRLConfidenceContent';
import SRLHoursContent from '../SRLHoursContent/SRLHoursContent';
import SRLChallengesContent from '../SRLChallengesContent/SRLChallengesContent';
import SRLAcademicContent from '../SRLAcademicContent/SRLAcademicContent';
import SRLGoalsStudentsContent from '../SRLGoalsStudentsContent/SRLGoalsStudentsContent';
import SRLSpacingAndPacingContent from '../SRLSpacingAndPacingContent/SRLSpacingAndPacingContent';
import SRLChallengeAndRecallContent from '../SRLChallengeAndRecallContent/SRLChallengeAndRecallContent';
import SRLHelpAndCollaborationContent from '../SRLHelpAndCollaborationContent/SRLHelpAndCollaborationContent';
import SRLEnvironmentAndMindsetContent from '../SRLEnvironmentAndMindsetContent/SRLEnvironmentAndMindsetContent';
import SRLGrowthAndLearning from '../SRLGrowthAndLearning/SRLGrowthAndLearning';
import SRLVirtualTable from '../SRLVirtualTable/SRLVirtualTable';
import { selectAssignmentDetailsData } from '../../../../utils';
import { viewInsights, closeInsights } from '../../../../../analyticsEvents';
import { checkReadingTabData } from '../../../../selectors/assignments';
import { getConfigObject } from '../../../../../util/config';
import { SRLSummary } from '../SRLSummary/SRLSummary';
import { useGrsAiSummariesPreference } from '../../../../hooks/useGrsAiSummariesPreference.js';
import style from './QQInsightsCard.module.css';

const STATUS_FRONT = 'FRONT';
const STATUS_INFO = 'INFO';

const QQInsightsCard = ({
  widget,
  title,
  subTitle,
  subTitle2,
  data,
  isLoading,
  nullStateMessage,
  nullStateImage,
  iconType,
  isDetailView,
  courseId,
  courseShortId,
  productModelId,
  assignmentId,
  activityId,
  tool,
  toggleInsightDetailsModal,
  sendAnalyticsEvent: analyticsEvent,
  reportType,
  SRLWidgetsHeight,
  isCheckedReadingTabData,
  assignments,
}) => {
  const dashboardWidgetId = get(widget, 'dashboardWidgetId');
  const detailClass = isDetailView ? style.detailView : '';
  const nullStateClass =
    !isLoading && nullStateMessage ? style.hasNullStateMessage : '';
  const [viewDetailDialogIsOpen, setViewDetailDialogIsOpen] = useState(false);
  const [showStatus, setShowStatus] = useState(STATUS_FRONT);
  const { FEATURE_USE_ASSIGNMENT_DETAIL_MODAL } = getConfigObject();

  const isSRLWidget = checkSRLWidget(widget.widgetType);
  const isSRLSummaryWidget = isSRLWidgetSummarizable(
    data?.isSRLIntroSurvey,
    widget?.widgetType
  );
  const { aiSummariesPreferenceEnabled } = useGrsAiSummariesPreference(
    courseId
  );

  const handleDetailsClick = () => {
    if (isSRLWidget || isAssignmentPerfWidget(widget.widgetType)) {
      setViewDetailDialogIsOpen(true);
    } else if (!isDetailView) {
      toggleInsightDetailsModal(dashboardWidgetId);
    }
    analyticsEvent(
      viewInsights({
        assignment_id: assignmentId,
      })
    );
  };

  const closeViewDetails = () => {
    setViewDetailDialogIsOpen(false);
    analyticsEvent(
      closeInsights({
        assignment_id: assignmentId,
      })
    );
  };

  const onShowInsightInfo = () => {
    setShowStatus(STATUS_INFO);
  };

  const onInfoBack = () => {
    setShowStatus(STATUS_FRONT);
  };

  const getClassNameImage = () => {
    switch (widget.widgetType) {
      case WIDGET_TYPE_SRL_CONFIDENCE_CHART:
        return style.nullStateImageConfidence;
      case WIDGET_TYPE_SRL_GOAL_CHART:
        return style.nullStateImageStudentGoals;
      case WIDGET_TYPE_SRL_HOURS_CHART:
        return style.nullStateImageHours;
      case WIDGET_TYPE_SRL_GROWTH_CHART:
        return style.nullStateImageGrowthAndLearning;
      case WIDGET_TYPE_SRL_CHALLENGES_CHART:
        return style.nullStateImageChallenges;
      case WIDGET_TYPE_SRL_ACADEMICS_CHART:
        return style.nullStateImageAcademics;
      case WIDGET_TYPE_SRL_SPACING_PACING_CHART:
        return style.nullStateSpaceAndPacing;
      case WIDGET_TYPE_SRL_CHALLENGE_AND_RECALL_CHART:
        return style.nullStateImageChallengesAndRecall;
      case WIDGET_TYPE_SRL_HELP_AND_COLLABORATION:
        return style.nullStateImageHelpAndCollaboration;
      case WIDGET_TYPE_SRL_ENVIRONMENT_AND_MINDSET:
        return style.nullStateImageEnvironmentAndMindset;

      default:
        return style.nullStateImage;
    }
  };

  const barChart = () => (
    <div>
      <div className={style.qqInsightsLearnObjectivesInner}>
        {data.insightsData.map((insight, idx) => (
          <QQBarAndLabel
            contentExpanded={idx === 0}
            key={insight.id}
            courseId={courseId}
            insightData={insight}
            isDetailView={isDetailView}
            reportType={reportType}
            sendAnalyticsEvent={sendAnalyticsEvent}
          />
        ))}
      </div>
      {!isDetailView && !nullStateMessage && (
        <QQLegend isDetailView={isDetailView} />
      )}
    </div>
  );

  const histogramChart = nullStateMessage ? (
    <div className={style.nullStateMessage}>{nullStateMessage}</div>
  ) : (
    <div className={style.QQScoreHistogramChart}>
      <QQScoreHistogramChart
        data={data.insightsData.assignmentsScore}
        average={data.insightsData.average}
        averageRange={data.insightsData.averageRange}
      />
    </div>
  );

  const loginAreaChart = () => <QQLoginPieChart data={data} />;
  const timeScoreChart = () => (
    <QQTimeOnTaskChart data={data} type={WIDGET_TYPE_TIME_SCORE_CHART} />
  );
  const timeAttemptsChart = () => (
    <QQTimeOnTaskChart data={data} type={WIDGET_TYPE_TIME_ATTEMPTS_CHART} />
  );
  const completedAssignment = () => <QQCompletedAssignment data={data} />;
  const srlConfidenceContent = () => (
    <SRLConfidenceContent data={data} isMobileXS={SRLWidgetsHeight > 426} />
  );
  const srlGoalsStudent = () => (
    <SRLGoalsStudentsContent data={data} isMobileXS={SRLWidgetsHeight > 426} />
  );
  const srlHoursContent = () => (
    <SRLHoursContent data={data} isMobileXS={SRLWidgetsHeight > 426} />
  );
  const srlGrowthAndLearning = () => (
    <SRLGrowthAndLearning data={data} isMobileXS={SRLWidgetsHeight > 426} />
  );
  const srlChallengesContent = () => (
    <SRLChallengesContent data={data} isMobileXS={SRLWidgetsHeight > 426} />
  );
  const srlAcademicContent = () => (
    <SRLAcademicContent data={data} isMobileXS={SRLWidgetsHeight > 426} />
  );
  const srlSpacingAndPacingContent = () => (
    <SRLSpacingAndPacingContent
      data={data}
      isMobileXS={SRLWidgetsHeight > 426}
    />
  );
  const srlChallengeAndRecallContent = () => (
    <SRLChallengeAndRecallContent
      data={data}
      isMobileXS={SRLWidgetsHeight > 426}
    />
  );
  const srlHelpAndCollaborationContent = () => (
    <SRLHelpAndCollaborationContent
      data={data}
      isMobileXS={SRLWidgetsHeight > 426}
    />
  );
  const srlEnvironmentAndMindsetContent = () => (
    <SRLEnvironmentAndMindsetContent
      data={data}
      isMobileXS={SRLWidgetsHeight > 426}
    />
  );

  const notBarCharts = [
    WIDGET_TYPE_LOGIN_PIE_CHART,
    WIDGET_TYPE_ASSIGNMENT_SCORE_HISTOGRAM_CHART,
    WIDGET_TYPE_TIME_SCORE_CHART,
    WIDGET_TYPE_TIME_ATTEMPTS_CHART,
    WIDGET_TYPE_COMPLETED_ASSIGNMENT,
  ];
  const isBarTypeWidget =
    !notBarCharts.includes(widget.widgetType) &&
    !isSRLWidget &&
    !isAssignmentPerfWidget(widget.widgetType);

  let cardDetailsData = [];

  if (FEATURE_USE_ASSIGNMENT_DETAIL_MODAL) {
    if (isAssignmentPerfWidget(widget.widgetType)) {
      cardDetailsData = selectAssignmentDetailsData(data.assignmentData);
    }
  }

  const hasSRLData = isSRLWidget && data?.insightsData?.length > 0;
  const showDetails =
    isBarTypeWidget || hasSRLData || cardDetailsData.length > 0;

  const InsightsCardBody = (
    <div className={style.buttonWrapper}>
      {widget.widgetType === WIDGET_TYPE_SRL_CONFIDENCE_CHART &&
        srlConfidenceContent()}
      {widget.widgetType === WIDGET_TYPE_SRL_HOURS_CHART && srlHoursContent()}
      {widget.widgetType === WIDGET_TYPE_SRL_CHALLENGES_CHART &&
        srlChallengesContent()}
      {widget.widgetType === WIDGET_TYPE_SRL_ACADEMICS_CHART &&
        srlAcademicContent()}
      {widget.widgetType === WIDGET_TYPE_SRL_GOAL_CHART && srlGoalsStudent()}
      {widget.widgetType === WIDGET_TYPE_SRL_GROWTH_CHART &&
        srlGrowthAndLearning()}
      {widget.widgetType === WIDGET_TYPE_SRL_SPACING_PACING_CHART &&
        srlSpacingAndPacingContent()}
      {widget.widgetType === WIDGET_TYPE_SRL_CHALLENGE_AND_RECALL_CHART &&
        srlChallengeAndRecallContent()}
      {widget.widgetType === WIDGET_TYPE_SRL_HELP_AND_COLLABORATION &&
        srlHelpAndCollaborationContent()}
      {widget.widgetType === WIDGET_TYPE_SRL_ENVIRONMENT_AND_MINDSET &&
        srlEnvironmentAndMindsetContent()}
      {widget.widgetType === WIDGET_TYPE_ASSIGNMENT_SCORE_HISTOGRAM_CHART &&
        histogramChart}
      {widget.widgetType === WIDGET_TYPE_LOGIN_PIE_CHART && loginAreaChart()}
      {widget.widgetType === WIDGET_TYPE_TIME_SCORE_CHART && timeScoreChart()}
      {widget.widgetType === WIDGET_TYPE_TIME_ATTEMPTS_CHART &&
        timeAttemptsChart()}
      {widget.widgetType === WIDGET_TYPE_COMPLETED_ASSIGNMENT &&
        completedAssignment()}
      {isBarTypeWidget && barChart()}
      {!isDetailView && !nullStateMessage && (
        <div
          className={clsx([style.qqInsightsCardFooter, style.qqGRSFooter])}
        >
          <QQInsightsFooterActions
            showDetails={showDetails}
            onInsightDetails={handleDetailsClick}
            isSRLWidget={isSRLWidget}
            widgetType={widget.widgetType}
            isCheckedReadingTabData={isCheckedReadingTabData}
            assignment={(assignments || []).find(
              (assignment) => assignment.id === assignmentId
            )}
            courseShortId={courseShortId}
            productModelId={productModelId}
            aiSummaryEnabled={aiSummariesPreferenceEnabled}
            isSRLSummaryWidget={isSRLSummaryWidget}
          />
        </div>
      )}
    </div>
  );

  const hideCardHeader = showStatus === STATUS_INFO || isDetailView;
  const cardHeader = hideCardHeader ? null : (
    <QQHeader
      title={title}
      subTitle={subTitle}
      subTitle2={subTitle2}
      iconType={iconType}
      showInsightInfo={onShowInsightInfo}
      widgetType={get(widget, 'widgetType', '')}
      preventFixedHeight={
        !nullStateMessage &&
        widget.widgetType === WIDGET_TYPE_ASSIGNMENT_SCORE_HISTOGRAM_CHART
      }
      preventWidthOverflow
      isSRLWidget={isSRLWidget}
    />
  );

  const InfoCard = (
    <div className={`${style.qqInsightsCard} ${nullStateClass}`}>
      {isLoading ? (
        <Loader />
      ) : (
        <Fragment>
          {cardHeader}
          {nullStateMessage && (
            <div className={style.nullStateMessage}>
              {nullStateImage && (
                <img
                  className={getClassNameImage()}
                  src={nullStateImage}
                  role="presentation"
                  alt={getClassNameImage()}
                />
              )}
              <div className={style.nullStateMessageContent}>
                {nullStateMessage}
              </div>
            </div>
          )}
          {!nullStateMessage && (
            <Fragment>{isLoading ? <Loader /> : InsightsCardBody}</Fragment>
          )}
        </Fragment>
      )}
    </div>
  );

  const BackCard = (
    <div className={`${style.qqInsightsCard} ${nullStateClass}`}>
      {showStatus === STATUS_INFO && cardHeader}
      {showStatus === STATUS_INFO && (
        <QQTimeVsScoreCalcInfo
          back={onInfoBack}
          widgetType={widget.widgetType}
        />
      )}
    </div>
  );

  const renderSRLVirtualTable = () => (
    <SRLVirtualTable
      widget={widget}
      courseId={courseId}
      courseShortId={courseShortId}
      productModelId={productModelId}
      assignmentId={assignmentId}
      activityId={activityId}
      tool={tool}
      isSRLIntroSurvey={data.isSRLIntroSurvey}
      isSRLEnglishVersion={data.isSRLEnglishVersion}
      insightsData={data.insightsData}
    />
  );

  const renderSRLSummary = () => (
    <SRLSummary
      widget={widget}
      courseId={courseId}
      assignmentId={assignmentId}
      isSRLIntroSurvey={data.isSRLIntroSurvey}
    />
  );

  const renderDialog = () => {
    const dialogContent = (
      <div className={style.dialogContent}>
        {renderSRLSummary()}
        {renderSRLVirtualTable()}
      </div>
    );

    return (
      <MLDialog
        title={isSRLWidget ? title : `Student Activity on ${subTitle}`}
        isOpen={viewDetailDialogIsOpen}
        handleClose={closeViewDetails}
      >
        {dialogContent}
      </MLDialog>
    );
  };

  const isFlipped = showStatus === STATUS_INFO;
  return (
    <Fragment>
      {renderDialog()}
      <div className={`${style.qqInsightsCardWrapper} ${detailClass}`}>
        <ReactCardFlip
          isFlipped={isFlipped}
          containerStyle={{ height: isSRLWidget ? SRLWidgetsHeight : 426 }}
          cardStyles={{ back: { zIndex: isFlipped ? '2' : '1' } }}
        >
          {InfoCard}
          {BackCard}
        </ReactCardFlip>
      </div>
    </Fragment>
  );
};

QQInsightsCard.propTypes = {
  title: PropTypes.string,
  subTitle: PropTypes.string,
  subTitle2: PropTypes.node,
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  widget: PropTypes.object,
  isDetailView: PropTypes.bool,
  courseId: PropTypes.string,
  courseShortId: PropTypes.string,
  productModelId: PropTypes.number,
  assignmentId: PropTypes.string,
  activityId: PropTypes.string,
  tool: PropTypes.string,
  iconType: PropTypes.string,
  nullStateMessage: PropTypes.node,
  nullStateImage: PropTypes.string,
  toggleInsightDetailsModal: PropTypes.func.isRequired,
  sendAnalyticsEvent: PropTypes.func,
  reportType: PropTypes.string,
  SRLWidgetsHeight: PropTypes.number,
  isCheckedReadingTabData: PropTypes.bool,
  assignments: PropTypes.array,
};

const mapStateToProps = (state) => {
  const SRLWidgetsHeight = get(state, 'insights.SRLWidgetsHeight');
  const assignments = get(state, 'courses.course.data.assignments', []);

  return {
    SRLWidgetsHeight,
    isCheckedReadingTabData: checkReadingTabData(state),
    assignments,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      sendAnalyticsEvent,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(QQInsightsCard);
